import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { tendersService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';
import { supabase } from '../../../lib/supabase';

const STATUS_OPTIONS = ['Open', 'Submitted', 'Under Evaluation', 'Won', 'Lost'];
const SECTOR_OPTIONS = ['Healthcare', 'Financial Services', 'Technology', 'Retail & E-Commerce', 'Manufacturing', 'Energy & Utilities', 'Telecommunications', 'Education', 'Government & Public Sector', 'Real Estate', 'Transportation & Logistics', 'Media & Entertainment', 'Professional Services', 'Non-Profit', 'Other'];

const STATUS_COLORS = {
  open: 'bg-primary/10 text-primary',
  submitted: 'bg-accent/10 text-accent',
  'under evaluation': 'bg-warning/10 text-warning',
  won: 'bg-success/10 text-success',
  lost: 'bg-error/10 text-error',
};

const STATUS_FLOW = ['Open', 'Submitted', 'Under Evaluation', 'Won', 'Lost'];

const TenderDetailDrawer = ({ tender, onClose, onUpdated, partners = [] }) => {
  const toast = useToast();
  const [mode, setMode] = useState('view'); // 'view' | 'edit' | 'delete'
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    name: tender?.name || '',
    partner_id: tender?.partner_id || '',
    sector: tender?.sector || '',
    value: tender?.rawValue || '',
    deadline: tender?.rawDeadline || '',
    tender_status: tender?.rawStatus || 'open',
    description: tender?.description || '',
  });

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handleSave = async () => {
    if (!form?.name?.trim()) { toast?.error('Tender name is required.'); return; }
    setSaving(true);
    try {
      await tendersService?.update(tender?.id, {
        name: form?.name,
        partner_id: form?.partner_id || null,
        sector: form?.sector,
        value: parseFloat(form?.value) || 0,
        deadline: form?.deadline || null,
        tender_status: form?.tender_status,
        description: form?.description,
      });
      toast?.success('Tender updated successfully');
      onUpdated?.();
      setMode('view');
    } catch (err) {
      toast?.error(err?.message || 'Failed to update tender');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { error } = await supabase?.from('tenders')?.delete()?.eq('id', tender?.id);
      if (error) throw error;
      toast?.success('Tender deleted');
      onUpdated?.();
      onClose();
    } catch (err) {
      toast?.error(err?.message || 'Failed to delete tender');
    } finally {
      setDeleting(false);
    }
  };

  if (!tender) return null;

  const currentStatusLabel = STATUS_OPTIONS?.find(s => s?.toLowerCase()?.replace(' ', '_') === form?.tender_status || s?.toLowerCase() === form?.tender_status) || tender?.status || 'Open';
  const statusKey = currentStatusLabel?.toLowerCase();

  const urgencyColor = tender?.daysLeft !== null && tender?.daysLeft <= 3
    ? 'text-error bg-error/10 border-error/20'
    : tender?.daysLeft !== null && tender?.daysLeft <= 7
    ? 'text-warning bg-warning/10 border-warning/20' :'text-slate-600 bg-slate-100 border-slate-200';

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full z-50 w-full max-w-[520px] bg-white shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-warning/5 to-primary/5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-warning to-primary flex items-center justify-center flex-shrink-0">
              <Icon name="FileText" size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-500 font-medium">RFQ / Tender</p>
              <h2 className="text-base font-bold text-slate-900 leading-tight truncate max-w-[300px]">{tender?.name}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {mode === 'view' && (
              <>
                <button onClick={() => setMode('edit')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors">
                  <Icon name="Pencil" size={13} /> Edit
                </button>
                <button onClick={() => setMode('delete')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-error/10 text-error text-xs font-semibold hover:bg-error/20 transition-colors">
                  <Icon name="Trash2" size={13} /> Delete
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        {/* Delete Confirmation */}
        {mode === 'delete' && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-error/10 flex items-center justify-center mb-4">
              <Icon name="Trash2" size={28} className="text-error" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Tender?</h3>
            <p className="text-sm text-slate-500 mb-2">You are about to permanently delete:</p>
            <p className="text-sm font-semibold text-slate-800 mb-6 px-4 py-2 bg-slate-50 rounded-xl">{tender?.name}</p>
            <p className="text-xs text-slate-400 mb-8">This action cannot be undone.</p>
            <div className="flex gap-3 w-full max-w-xs">
              <button onClick={() => setMode('view')} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-2.5 rounded-xl bg-error text-white text-sm font-semibold hover:bg-error/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {deleting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Deleting...</> : <><Icon name="Trash2" size={14} /> Delete</>}
              </button>
            </div>
          </div>
        )}

        {/* View Mode */}
        {mode === 'view' && (
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${STATUS_COLORS?.[statusKey] || 'bg-slate-100 text-slate-600'}`}>
                {tender?.status}
              </span>
              {tender?.daysLeft !== null && (
                <span className={`px-3 py-1 rounded-lg text-xs font-semibold border flex items-center gap-1 ${urgencyColor}`}>
                  <Icon name="Clock" size={11} /> {tender?.daysLeft} days left
                </span>
              )}
            </div>

            {/* Submission Workflow */}
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-xs font-semibold text-slate-500 mb-3 flex items-center gap-1.5"><Icon name="GitBranch" size={12} />Submission Workflow</p>
              <div className="flex items-center gap-0">
                {STATUS_FLOW?.map((s, i) => (
                  <React.Fragment key={s}>
                    <div className="flex flex-col items-center">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        s === tender?.status ? 'border-primary bg-primary'
                          : STATUS_FLOW?.indexOf(s) < STATUS_FLOW?.indexOf(tender?.status) ? 'border-success bg-success' :'border-slate-200 bg-white'
                      }`}>
                        {STATUS_FLOW?.indexOf(s) < STATUS_FLOW?.indexOf(tender?.status) ? (
                          <Icon name="Check" size={9} className="text-white" />
                        ) : s === tender?.status ? (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        ) : null}
                      </div>
                      <span className={`text-xs mt-1 font-medium whitespace-nowrap ${s === tender?.status ? 'text-primary' : 'text-slate-400'}`} style={{ fontSize: '10px' }}>
                        {s}
                      </span>
                    </div>
                    {i < STATUS_FLOW?.length - 1 && (
                      <div className={`flex-1 h-px mx-1 mb-4 ${STATUS_FLOW?.indexOf(s) < STATUS_FLOW?.indexOf(tender?.status) ? 'bg-success' : 'bg-slate-200'}`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Key Info Grid */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: 'Building2', label: 'Client', value: tender?.client || '—' },
                { icon: 'DollarSign', label: 'Value', value: tender?.value || '—' },
                { icon: 'Calendar', label: 'Deadline', value: tender?.deadline || 'TBD' },
                { icon: 'Layers', label: 'Sector', value: tender?.sector || '—' },
              ]?.map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-3.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon name={item?.icon} size={13} className="text-slate-400" />
                    <span className="text-xs text-slate-500 font-medium">{item?.label}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900">{item?.value}</p>
                </div>
              ))}
            </div>

            {/* Roles */}
            <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 rounded-xl">
              <div>
                <p className="text-xs text-slate-500 font-medium mb-1">Technical Role</p>
                <p className="text-sm font-semibold text-slate-800">{tender?.technicalRole || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium mb-1">Financial Role</p>
                <p className="text-sm font-semibold text-slate-800">{tender?.financialRole || '—'}</p>
              </div>
            </div>

            {/* Description */}
            {tender?.description && (
              <div className="p-3.5 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-500 font-medium mb-1.5 flex items-center gap-1.5"><Icon name="FileText" size={13} />Description</p>
                <p className="text-sm text-slate-700 leading-relaxed">{tender?.description}</p>
              </div>
            )}
          </div>
        )}

        {/* Edit Mode */}
        {mode === 'edit' && (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Tender / RFQ Name <span className="text-red-400">*</span></label>
              <input name="name" value={form?.name} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all" />
            </div>

            {/* Partner */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Partner</label>
              <select name="partner_id" value={form?.partner_id} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white transition-all">
                <option value="">— No Partner —</option>
                {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
              </select>
            </div>

            {/* Sector */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Sector</label>
              <select name="sector" value={form?.sector} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white transition-all">
                <option value="">Select sector...</option>
                {SECTOR_OPTIONS?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* Value + Deadline */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Value ($)</label>
                <input name="value" type="number" min="0" value={form?.value} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Deadline</label>
                <input name="deadline" type="date" value={form?.deadline} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Status</label>
              <div className="grid grid-cols-3 gap-2">
                {STATUS_OPTIONS?.map(s => {
                  const dbVal = s?.toLowerCase()?.replace(' ', '_');
                  const isSelected = form?.tender_status === dbVal || form?.tender_status === s?.toLowerCase();
                  return (
                    <button key={s} type="button"
                      onClick={() => setForm(p => ({ ...p, tender_status: dbVal }))}
                      className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${isSelected ? `${STATUS_COLORS?.[s?.toLowerCase()] || 'bg-primary/10 text-primary'} border-current shadow-sm` : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Description</label>
              <textarea name="description" value={form?.description} onChange={handleChange} rows={3} className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none transition-all" />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 bg-slate-50">
          {mode === 'view' && (
            <button onClick={onClose} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-white transition-colors">
              Close
            </button>
          )}
          {mode === 'edit' && (
            <div className="flex gap-3">
              <button onClick={() => setMode('view')} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-white transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-warning to-primary text-white text-sm font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</> : <><Icon name="Save" size={14} /> Save Changes</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TenderDetailDrawer;
